import { FC, useState, useEffect } from "react"
import { Button, Form, Input, Typography, InputNumber } from "antd"
import { useStore } from "stores/utils/use-store"
import { observer } from "mobx-react"
import { WinNotificationsResponse, WinNotificationType, winNotificationButton } from "../store/types"
import { UploadComponent, SubmitCancelButtons, DefaultLinksRouting, PixelsEditorV2 } from "components"
import { Pixel } from "domains/managingJson/store/types"
import Styles from "./styles.module.css"
import { notifications } from "notifications"

interface Props {
  onClose: () => void
  data?: WinNotificationsResponse
}

const DISABLE_ICON_UPLOAD = true

export const WinNotificationCreate: FC<Props> = observer(({ onClose, data }) => {
  const { dataStore: { winNotificationsStore } } = useStore()
  const [form] = Form.useForm()
  const [mainImageUrl, setMainImageUrl] = useState("")
  const [iconImageUrl, setIconImageUrl] = useState("")

  const [listButtons, setListButtons] = useState<winNotificationButton[]>([])

  const [trackingPixels, setTrackingPixels] = useState<Pixel[]>([])

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        name: data.name,
        title: data.title,
        body: data.body,
        timeToLive: data.timeToLive,
        buttonText: "",
        buttonUrl: "",
        buttonName: "",
      })
      setMainImageUrl(data.mainImageUrl || "")
      setIconImageUrl(data?.iconImageUrl || "")
      setListButtons(data.buttons || [])
      setTrackingPixels(data.pixels || [])
    }
  }, [data, form])

  const onFinishHandler = async(values: Partial<WinNotificationType>) => {
    // ensure uniqueness if new
    if (
      !data
      && winNotificationsStore.notifications.some(item => item.title === values.title)
    ) {
      return notifications.error("Title must be unique!")
    }

    const newData: WinNotificationType = {
      name: values.name || "",
      title: values.title || "",
      body: values.body || "",
      timeToLive: values.timeToLive,
      mainImageUrl,
      iconImageUrl: DISABLE_ICON_UPLOAD ? undefined : iconImageUrl,
      buttons: listButtons,
      pixels: trackingPixels,
    }

    winNotificationsStore.funcAfterSuccess = async() => {
      notifications.success(data ? "Updated successfully" : "Created successfully")
      onClose()
      form.resetFields()
      await winNotificationsStore.getData()
    }

    if (data?.id) {
      await winNotificationsStore.updateNotification(data.id, newData)
    } else {
      await winNotificationsStore.createNotification(newData)
    }
  }

  const addButton = () => {
    const { buttonText, buttonUrl, buttonName } = form.getFieldsValue(["buttonText", "buttonUrl", "buttonName"])
    if (!buttonText?.trim()) return // Skip if empty
    if (listButtons.length >= 2) {
      return notifications.error("You can only add up to 2 buttons.")
    }

    const newBtn: winNotificationButton = {
      name: buttonName?.trim() || "",
      text: buttonText.trim(),
      url: buttonUrl?.trim() || "#",
    }
    setListButtons([...listButtons, newBtn])

    // Clear fields in form (so next button starts fresh)
    form.setFieldsValue({ buttonText: "", buttonUrl: "", buttonName: "" })
  }

  const removeButton = (idx: number) => {
    setListButtons(listButtons.filter((_, i) => i !== idx))
  }

  const moveItem = <T, >(list: T[], index: number, direction: "up" | "down", setList: (newList: T[]) => void) => {
    const targetIndex = direction === "up" ? index - 1 : index + 1

    // Ensure the target index is within bounds
    if (targetIndex < 0 || targetIndex >= list.length) return

    // Swap the items
    const newList = [...list]
    const temp = newList[index]
    newList[index] = newList[targetIndex]
    newList[targetIndex] = temp

    setList(newList)
  }

  return (
    <Form
      form={form}
      onFinish={onFinishHandler}
      className={Styles.form}
      layout="vertical"
    >
      <Form.Item
        label="Name"
        name="name"
        rules={[
          { required: true, message: "Name is required" },
          { max: 100, message: "Max length is 100" },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Title"
        name="title"
        rules={[
          { required: true, message: "Title is required" },
          { max: 100, message: "Max length is 100" },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Body"
        name="body"
        rules={[
          { required: true, message: "Body is required" },
        ]}
      >
        <Input.TextArea rows={4} />
      </Form.Item>

      <Form.Item
        label="Time to Live"
        name="timeToLive"
      >
        <InputNumber min={1} />
      </Form.Item>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <Form.Item label="Main Image" style={{ flex: 1 }}>
          <UploadComponent
            image={mainImageUrl}
            setImage={setMainImageUrl}
            maxSize={0.5}
          />
        </Form.Item>
        <Form.Item
          label={
            DISABLE_ICON_UPLOAD ? <span>Icon Image <Typography.Text type="danger">(disabled)</Typography.Text></span> : "Icon Image"
          }
          style={{ flex: 1 }}
        >
          <UploadComponent
            disabled={DISABLE_ICON_UPLOAD}
            image={iconImageUrl}
            setImage={setIconImageUrl}
            maxSize={0.5}
          />
        </Form.Item>
      </div>

      {/* Buttons */}
      <Form.Item label="Buttons">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Form.Item name="buttonName" noStyle>
            <Input placeholder="Button name" />
          </Form.Item>
          <Form.Item name="buttonText" noStyle>
            <Input placeholder="Button text" />
          </Form.Item>
          <Form.Item name="buttonUrl" noStyle>
            <DefaultLinksRouting link="" formItemName="buttonUrl" displaySelect notRequired />
          </Form.Item>
          <Button onClick={addButton}>Add Button</Button>
        </div>

        {listButtons.map((btn, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
            <Typography.Text style={{ marginRight: 8 }}>
              Name: {btn.name} | Text: {btn.text} | URL: {btn.url}
            </Typography.Text>
            <div style={{ display: "flex", gap: 4 }}>
              <Button onClick={() => moveItem(listButtons, i, "up", setListButtons)} disabled={i === 0}>
                ↑
              </Button>
              <Button onClick={() => moveItem(listButtons, i, "down", setListButtons)} disabled={i === listButtons.length - 1}>
                ↓
              </Button>
              <Button danger onClick={() => removeButton(i)}>
                Remove
              </Button>
            </div>
          </div>
        ))}
      </Form.Item>

      <Form.Item label="Tracking Pixels">
        <PixelsEditorV2
          pixels={trackingPixels}
          onChange={setTrackingPixels}
        />
      </Form.Item>

      <SubmitCancelButtons
        onClose={onClose}
        isLoading={winNotificationsStore.isLoading}
      />
    </Form>
  )
})
